import { Component, OnInit } from '@angular/core'
import { ActivatedRoute, ParamMap, Router } from '@angular/router'
import {
  classifyConnectError,
  completeConnectPayload,
  connectFailureMessage,
  takeConnectIdentity,
} from '@employer/app/modules/marketing-channels/channel-identity.util'
import { MarketingChannelsService } from '@employer/app/modules/marketing-channels/marketing-channels.service'
import { oauthCallbackAction, oauthConnectTarget } from '@employer/app/modules/marketing-channels/oauth-redirect.util'
import { environment } from '@employer/environments/environment'
import { NotificationHelper } from '@engineering11/web-ui-helpers'

const CHANNELS_ROUTE = '/settings/marketing-channels'

@Component({
  selector: 'app-oauth-callback',
  templateUrl: './oauth-callback.component.html',
})
export class OAuthCallbackComponent implements OnInit {
  error = false

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private svc: MarketingChannelsService,
    private notifications: NotificationHelper
  ) {}

  ngOnInit(): void {
    const channelId = this.route.snapshot.paramMap.get('channelId')
    const code = this.route.snapshot.queryParamMap.get('code')
    const state = this.route.snapshot.queryParamMap.get('state') ?? undefined
    const providerError = this.route.snapshot.queryParamMap.get('error')

    if (channelId) {
      const action = oauthCallbackAction(channelId, state, allQueryParams(this.route.snapshot.queryParamMap), window.location)
      if (action.kind === 'relay') {
        window.location.replace(action.url)
        return
      }
      if (action.kind === 'reject') {
        takeConnectIdentity(sessionStorage, channelId)
        this.fail()
        return
      }
    }

    // Read (and clear) the handoff even when the round trip failed, so an abandoned attempt can never
    // steer the next one.
    const identityType = channelId ? takeConnectIdentity(sessionStorage, channelId) : undefined

    if (providerError || !channelId || !code) {
      this.fail()
      return
    }

    const { redirectUri } = oauthConnectTarget(channelId, window.location, environment.tenant.defaultTenant)
    this.svc.completeConnect(channelId, completeConnectPayload(code, state, redirectUri, identityType)).subscribe({
      next: result => {
        this.notifications.popNotification({
          type: 'success',
          title: 'Channel connected',
          message: identityType === 'member' ? 'Your personal account is now connected.' : `${result.displayName ?? 'The channel'} is now connected.`,
        })
        this.router.navigateByUrl(CHANNELS_ROUTE)
      },
      error: (err: unknown) => this.fail(err),
    })
  }

  returnToChannels(): void {
    this.router.navigateByUrl(CHANNELS_ROUTE)
  }

  private fail(err?: unknown): void {
    this.error = true
    this.notifications.popNotification({
      type: 'error',
      title: 'Connection failed',
      message: connectFailureMessage(classifyConnectError(err)),
    })
  }
}

function allQueryParams(params: ParamMap): URLSearchParams {
  const query = new URLSearchParams()
  for (const key of params.keys) params.getAll(key).forEach(value => query.append(key, value))
  return query
}
